import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { KPICard, KPIGrid } from '../../components/admin/KPICards';
import { 
  MonthlyEvolutionChart, 
  ConversionBySourceChart, 
  StatusPieChart,
  ConversionTrendChart,
  TopReferrersChart
} from '../../components/admin/Charts';
import { TrendingUp, Users, Target, Award, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, monthlyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/stats/monthly`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setMonthly(monthlyRes.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <AdminLayout
        title="Analíticas"
        subtitle="Cargando..."
        onLogout={handleLogout}
      >
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const conversionRate = stats?.conversion_rate || 0;
  const avgReferralsPerClient = stats?.total_clients > 0 
    ? (stats.total_referrals / stats.total_clients).toFixed(1) 
    : 0;

  return (
    <AdminLayout
      title="Analíticas Avanzadas"
      subtitle="Métricas y tendencias del programa de referidos"
      pendingRewards={stats?.status_counts?.validado || 0}
      onLogout={handleLogout}
    >
      {/* Key Metrics */}
      <KPIGrid className="mb-8">
        <KPICard
          title="Tasa de Conversión"
          value={`${conversionRate}%`}
          subtitle="Referidos validados / Total"
          icon={TrendingUp}
          variant="highlight"
        />
        <KPICard
          title="Referidos por Cliente"
          value={avgReferralsPerClient}
          subtitle="Promedio"
          icon={Users}
        />
        <KPICard
          title="Leads Recientes"
          value={stats?.recent_leads_7d || 0}
          subtitle="Últimos 7 días"
          icon={Target}
          trend={stats?.recent_leads_7d > 0 ? 'up' : 'neutral'}
        />
        <KPICard
          title="Top Performer"
          value={stats?.top_referrers?.[0]?.total_referrals || 0}
          subtitle={stats?.top_referrers?.[0]?.name || 'N/A'}
          icon={Award}
        />
      </KPIGrid>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <MonthlyEvolutionChart data={monthly} />
        <StatusPieChart data={stats?.status_counts} />
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ConversionBySourceChart />
        <ConversionTrendChart />
      </div>

      {/* Top Referrers */}
      <div className="grid grid-cols-1 gap-6">
        <TopReferrersChart data={stats?.top_referrers} />
      </div>

      {/* Additional Stats */}
      <div className="mt-8 p-6 rounded-lg bg-[#0A0A0A] border border-white/10">
        <h3 className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-6">
          Resumen del Funnel
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {[
            { label: 'Leads', key: 'nuevo_lead', color: 'bg-blue-500' },
            { label: 'Registrados', key: 'registrado', color: 'bg-blue-400' },
            { label: 'Contactados', key: 'contactado', color: 'bg-yellow-500' },
            { label: 'Visitas', key: 'prueba_agendada', color: 'bg-orange-500' },
            { label: 'Instalados', key: 'instalado', color: 'bg-purple-500' },
            { label: 'Activos', key: 'activo', color: 'bg-cyan-500' },
            { label: 'Validados', key: 'validado', color: 'bg-emerald-500' },
            { label: 'Completados', key: 'recompensa_entregada', color: 'bg-white' },
          ].map(({ label, key, color }) => (
            <div key={key} className="text-center p-3 rounded-lg bg-white/5">
              <div className={`w-3 h-3 rounded-full ${color} mx-auto mb-2`} />
              <p className="text-xl font-light text-white font-['Outfit']">
                {stats?.status_counts?.[key] || 0}
              </p>
              <p className="text-xs text-zinc-500">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
